import { Injectable, inject } from '@angular/core';
import {
  Router,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart
} from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, timeout } from 'rxjs/operators';
import { AppState } from '../../ngrx/template/template.reducers';
import { SplashScreenService } from './splash-screen.service';
import { ModalService } from '../modal/modal.service';
import { AuthService } from '../auth/auth.service';
import {
  extractTemplateSlug,
  isTemplateSlugReady,
} from '../../utils/template-cache.util';

@Injectable({
  providedIn: 'root'
})
export class SplashScreenCoordinatorService {
  private readonly router = inject(Router);
  private readonly store = inject(Store<AppState>);
  private readonly splashScreenService = inject(SplashScreenService);
  private readonly modalService = inject(ModalService);
  private readonly authService = inject(AuthService);

  private initialized = false;
  private pendingNavigations = new Set<number>();
  private resumeTimeout: ReturnType<typeof setTimeout> | null = null;

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.splashScreenService.show();

    this.authService.authStateReady$
      .pipe(filter((ready) => ready), take(1))
      .subscribe(() => {
        if (this.isNonTemplateRoute(this.router.url)) {
          this.splashScreenService.hideAfterPaint();
        }
      });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.pendingNavigations.add(event.id);

        const destinationUrl = event.url;
        if (
          !this.isNonTemplateRoute(destinationUrl) &&
          !this.isRouteAlreadyLoaded(destinationUrl)
        ) {
          this.splashScreenService.show();
        }
        return;
      }

      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.pendingNavigations.delete(event.id);

        if (this.pendingNavigations.size === 0) {
          const url = event instanceof NavigationEnd ? event.urlAfterRedirects : this.router.url;
          this.waitForPageShellReady(url).subscribe(() =>
            this.splashScreenService.hideAfterPaint()
          );
        }
      }
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.handleAppResume();
      }
    });

    window.addEventListener('pageshow', event => {
      if (event.persisted) {
        this.handleAppResume();
      }
    });
  }

  private handleAppResume(): void {
    if (this.resumeTimeout) {
      clearTimeout(this.resumeTimeout);
    }

    if (this.modalService.hasActiveModals() || this.isRouteAlreadyLoaded(this.router.url)) {
      return;
    }

    this.splashScreenService.show();

    this.resumeTimeout = setTimeout(() => {
      this.waitForPageShellReady(this.router.url).subscribe(() =>
        this.splashScreenService.hideAfterPaint()
      );
      this.resumeTimeout = null;
    }, 0);
  }

  private isRouteAlreadyLoaded(url: string): boolean {
    if (this.isNonTemplateRoute(url)) {
      return true;
    }

    return isTemplateSlugReady(this.store, extractTemplateSlug(url));
  }

  /** Wait for template store state, then for the CMS shell to paint in the DOM. */
  private waitForPageShellReady(url: string): Observable<void> {
    if (this.isNonTemplateRoute(url)) {
      return of(undefined);
    }

    return this.waitForRouteReady(url).pipe(
      switchMap(() => {
        if (!this.isRouteAlreadyLoaded(url)) {
          return of(undefined);
        }
        return this.pollUntilShellRendered();
      })
    );
  }

  private waitForRouteReady(url: string): Observable<void> {
    const slug = extractTemplateSlug(url);

    return this.store.select(state => state.template.slugs[slug]?.loaded === true).pipe(
      filter(Boolean),
      take(1),
      timeout(15000),
      catchError(() => of(undefined)),
      map(() => undefined)
    );
  }

  private pollUntilShellRendered(): Observable<void> {
    return new Observable((observer) => {
      const deadline = Date.now() + 8000;

      const tick = () => {
        const grid = document.querySelector('.dynamic-template .grid-container');
        const hasGridContent = !!grid && grid.children.length > 0;
        const hasDynamicComponent = !!document.querySelector('app-dynamic-component');

        if (hasGridContent || hasDynamicComponent || Date.now() > deadline) {
          observer.next(undefined);
          observer.complete();
          return;
        }

        requestAnimationFrame(tick);
      };

      tick();
    });
  }

  private isNonTemplateRoute(url: string): boolean {
    const path = extractTemplateSlug(url);
    return path === 'logout'
      || path === 'login'
      || path === '401'
      || path === ''
      || path === 'template-generator'
      || path.startsWith('media/');
  }
}
